import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Recopilamos información personal que usted nos proporciona directamente al realizar una compra, como su nombre, dirección de correo electrónico e información de pago. No almacenamos datos de tarjetas de crédito en nuestros servidores; estos son procesados de forma segura por nuestro proveedor de pagos.'
  }, {
    heading: 'Uso de la Información',
    text: 'Utilizamos su información personal únicamente para procesar su compra, enviarle el producto digital adquirido y comunicarnos con usted sobre su pedido. No vendemos, alquilamos ni compartimos su información personal con terceros con fines de marketing.'
  }, {
    heading: 'Cookies y Tecnologías de Seguimiento',
    text: 'Nuestro sitio web puede utilizar cookies y tecnologías similares para mejorar su experiencia de navegación y analizar el tráfico del sitio. Puede configurar su navegador para rechazar cookies, aunque esto podría afectar la funcionalidad del sitio.'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
  }, {
    heading: 'Sus Derechos',
    text: 'Usted tiene derecho a acceder, corregir o eliminar su información personal en cualquier momento. Para ejercer estos derechos, puede contactarnos a través de los canales indicados en nuestro sitio.'
  }, {
    heading: 'Cambios a Esta Política',
    text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Cualquier cambio será publicado en esta página con la fecha de la última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Aceptación de los Términos',
    text: 'Al acceder a este sitio web y realizar una compra, usted acepta estar sujeto a estos términos y condiciones de uso. Si no está de acuerdo con alguno de estos términos, le rogamos que no utilice nuestro sitio.'
  }, {
    heading: 'Producto Digital',
    text: 'El producto "SOS: Bebé Duerme (Edición USA)" es un manual digital en formato descargable. Al completar su compra, recibirá acceso inmediato al contenido. Debido a la naturaleza digital del producto, todas las ventas son finales.'
  }, {
    heading: 'Uso Personal',
    text: 'El contenido adquirido es para uso personal y no comercial. Queda prohibida la reproducción, distribución, modificación o reventa del material sin autorización expresa por escrito.'
  }, {
    heading: 'Descargo de Responsabilidad',
    text: 'La información proporcionada en este producto es de carácter educativo e informativo. No sustituye el consejo médico profesional. Consulte siempre con su pediatra antes de implementar cambios en la rutina de sueño de su bebé. Los resultados pueden variar según cada familia y situación individual.'
  }, {
    heading: 'Propiedad Intelectual',
    text: 'Todo el contenido de este sitio web y del producto digital, incluyendo textos, gráficos, logotipos e imágenes, está protegido por derechos de autor y es propiedad exclusiva de SOS: Bebé Duerme.'
  }, {
    heading: 'Ley Aplicable',
    text: 'Estos términos se rigen por las leyes de los Estados Unidos de América. Cualquier disputa será resuelta en los tribunales competentes de dicha jurisdicción.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-80">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-81" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: [0.21, 0.47, 0.32, 0.98]
    }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl max-h-[80vh] bg-navy-700 border border-gold/20 rounded-2xl shadow-2xl overflow-hidden" data-id="element-82">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 bg-navy-700 border-b border-gold/10" data-id="element-83">
          <h2 className="text-xl md:text-2xl font-bold text-cream font-serif" data-id="element-84">
            {content.title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full text-muted hover:text-gold hover:bg-navy-800 transition-colors" aria-label="Cerrar" data-id="element-85">
            <XIcon className="w-5 h-5" data-id="element-86" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-72px)] space-y-6" data-id="element-87">
          {content.sections.map((section, i) => <div key={i} data-id="element-88">
              <h3 className="text-gold font-semibold text-base mb-2 font-serif" data-id="element-89">
                {section.heading}
              </h3>
              <p className="text-muted text-sm leading-relaxed" data-id="element-90">
                {section.text}
              </p>
            </div>)}
          <p className="text-muted/60 text-xs pt-4 border-t border-navy-800" data-id="element-91">
            Última actualización: Marzo 2026
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
export function FooterSection() {
  const currentYear = new Date().getFullYear();
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 px-4 bg-navy-900 border-t border-navy-800" data-id="element-92">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-4" data-id="element-93">
          <div className="flex items-center gap-6" data-id="element-94">
            <button onClick={() => setActiveModal('privacy')} className="text-muted text-sm hover:text-gold transition-colors cursor-pointer" data-id="element-95">
              Política de Privacidad
            </button>
            <span className="text-navy-700" data-id="element-96">•</span>
            <button onClick={() => setActiveModal('terms')} className="text-muted text-sm hover:text-gold transition-colors cursor-pointer" data-id="element-97">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-muted/60 text-xs" data-id="element-98">
            SOS: Bebé Duerme © {currentYear}. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-99">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-100" />}
      </AnimatePresence>
    </>;
}